"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { BaseDecimal, Chain, SwapKitNumber } from "@swapkit/core";
import { keepPreviousData } from "@tanstack/react-query";
import { AnimatePresence, motion, useAnimate } from "framer-motion";

import { invariant } from "@coinbot/forge-api/helpers";
import {
  transferProfitToForgeNetworkFee,
  transferTNSToBuyerNetworkFee,
} from "@coinbot/forge-api/models";

import Anvil from "~/assets/anvil.svg";
import { ListingRowItem } from "~/components/common/ListingRowItem";
import { PriceWithCurrency } from "~/components/common/PriceWithCurrency";
import { showShareOnSocialMediaModal, showSuccessModal } from "~/components/modals";
import { useCart, useCartPopover } from "~/context/cart";
import { useConfetti } from "~/context/confetti";
import { useSwapKit } from "~/context/swapkit";
import { api } from "~/services/api";
import { cn } from "~/services/shadcn";
import { Empty } from "../../common/Empty";
import { Loading } from "../../common/Loading";
import { Button, Popover, PopoverClose, PopoverContent, PopoverTrigger } from "../../ui";
import { confirmAction } from "../../ui/confirm-action";

export const NavbarCart = () => {
  const [isBuying, setIsBuying] = useState(false);
  const swapkit = useSwapKit();
  const { cart, clearCart, removeCartItem } = useCart();
  const popover = useCartPopover();
  const [scope, animate] = useAnimate();
  const router = useRouter();
  const { popConfetti } = useConfetti();
  const { data: priceByToken } = api.price.getByToken.useQuery();

  const cartListingIds = Array.from(cart?.cartItemsById?.keys?.() || []).map(Number);

  const { mutateAsync: updateTransferIntent } = api.tns.updateTransferIntent.useMutation();
  const { data: listings, isLoading: isLoadingCart } = api.listings.all.useQuery(
    {
      limit: cartListingIds?.length,
      filters: {
        ids: cartListingIds,
        modelTypes: ["listings"],
        status: "active",
      },
    },
    { enabled: cartListingIds?.length > 0, placeholderData: keepPreviousData },
  );

  const joinedListingIds = cartListingIds.join(",");

  useEffect(() => {
    if (joinedListingIds === "") return;

    // eslint-disable-next-line
    animate(scope.current, { scale: [1, 1.2, 1] }, { duration: 0.3 });
  }, [scope, joinedListingIds, animate]);

  // TODO: move listings logic and fetching to `useCart()` hook
  const filteredListings = cartListingIds
    ?.map((id) => {
      const matchingListing = listings?.find((listing) => listing?.id === id);

      if (matchingListing?.owner === swapkit?.connectedWallet?.address) return;

      return matchingListing;
    })
    .filter(Boolean);

  const cartItemsCount = filteredListings?.length || 0;
  const isEmptyCart = cartItemsCount <= 0;

  const cartTotalInRune = filteredListings?.reduce(
    (acc, listing) => (acc += listing?.price?.rune || 0),
    0,
  );

  const hasInsufficientBalance =
    parseFloat(swapkit.connectedWallet?.balance || `0`) < cartTotalInRune;

  const handleConfirmPurchase = useCallback(async () => {
    await confirmAction({
      variant: "CART_PURCHASE",
    });

    setIsBuying(true);

    const firstListing = filteredListings?.[0];

    invariant(firstListing, "No listing found in cart");
    invariant(firstListing?.price?.rune, "No price found for listing");

    const amountWithFees = new SwapKitNumber({
      value: firstListing?.price?.rune,
      decimal: BaseDecimal[Chain.THORChain],
    })
      .add(transferTNSToBuyerNetworkFee)
      .add(transferProfitToForgeNetworkFee.div(2));

    invariant(swapkit.connectedWallet?.address, "You must be connected to a wallet to purchase");
    invariant(
      swapkit.connectedWallet?.address !== firstListing?.owner,
      "You cannot buy your own listing!",
    );

    try {
      invariant(firstListing?.status === "active", "Listing must have status 'active' to purchase");

      const txHash = await swapkit.transferToEscrowWallet({
        escrowWalletAddress: firstListing?.escrowWallet?.address,
        amount: amountWithFees,
      });

      invariant(txHash, "No transaction hash returned. Transfer was rejected or failed.");

      await updateTransferIntent({
        listingId: firstListing?.id || 0,
        txHash,
        type: "buy_transfer",
        buyer: swapkit.connectedWallet?.address,
      });

      popover?.setIsOpen(false);
      clearCart();

      router.push("/profile");

      void popConfetti();
      await showSuccessModal({ variant: "LISTING_PURCHASED", tns: firstListing });
      await showShareOnSocialMediaModal({ variant: "LISTING_PURCHASED", tns: firstListing });
    } catch (err) {
      console.error(err);
    } finally {
      setIsBuying(false);
    }
  }, [router, filteredListings, swapkit, updateTransferIntent, popover, clearCart, popConfetti]);

  const submitCartButtonProps = useMemo(() => {
    if (isEmptyCart) {
      return {
        children: (
          <Link href="/market" onClick={() => popover.close()}>
            Explore Market
          </Link>
        ),
        asChild: true,
      } as const;
    }

    if (!swapkit.connectedWallet?.address) {
      // TODO: open connect wallet popover
      return { children: "Connect Wallet", disabled: true, onClick: () => null };
    }

    if (hasInsufficientBalance) {
      return { children: "Insufficient Balance", disabled: true, variant: "outline" } as const;
    }

    return {
      disabled: isBuying || isLoadingCart,
      isLoading: isBuying,
      loadingText: "Completing Transfer...",
      children: "Confirm Purchase",
      onClick: handleConfirmPurchase,
    } as const;
  }, [
    isLoadingCart,
    popover,
    hasInsufficientBalance,
    isEmptyCart,
    handleConfirmPurchase,
    isBuying,
    swapkit.connectedWallet?.address,
  ]);

  return (
    <Popover open={popover?.isOpen} onOpenChange={popover?.setIsOpen} modal>
      <PopoverTrigger>
        <motion.div ref={scope} className="relative flex h-6 w-6 shrink-0 items-center">
          <Anvil className="h-auto w-full fill-white" />

          <div className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center bg-orange-gradient text-[10px] font-medium leading-none text-black">
            {cartItemsCount}
          </div>
        </motion.div>
      </PopoverTrigger>

      <PopoverContent
        className="flex max-h-[var(--radix-popover-content-available-height)] w-[100vw] md:w-[580px] md:max-w-[580px]"
        side="bottom"
        sideOffset={26}
        align="end"
        alignOffset={-21}
      >
        <PopoverClose />

        <div className="flex w-full flex-col p-3 md:p-5">
          <div className="mt-4 flex items-center border-b border-[#2D2226] px-2.5 pb-3 md:mt-6">
            {/* <ArrowLeft className="w-4 text-[#6F767E]" /> */}

            <div className="text-lg font-medium uppercase">Your cart</div>

            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => clearCart()}>
              Clear Cart
            </Button>
          </div>

          <div
            className={cn(
              "mt-2 flex max-h-full flex-col",
              !isEmptyCart && "overflow-y-auto md:h-[360px]",
            )}
          >
            <AnimatePresence mode="popLayout">
              {isLoadingCart ? (
                <Loading className="mx-auto mt-5" />
              ) : isEmptyCart ? (
                <Empty className="mx-auto -mb-2 mt-5">No items in Cart</Empty>
              ) : (
                filteredListings?.map((cartListing) => (
                  <motion.div
                    layout
                    key={`cart-listing-${cartListing?.id}-${cartListing?.name}`}
                    initial={{ opacity: 1, transform: "scale(1)" }}
                    exit={{ opacity: 0, transform: "scale(0.9)" }}
                  >
                    <ListingRowItem
                      onRemove={() => removeCartItem(`${cartListing?.id}`)}
                      tns={cartListing}
                    />
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          {!isEmptyCart && (
            <div className="mt-2 flex border-t border-[#2D2226] px-2.5 pt-8">
              <div className="text-lg font-medium uppercase">Purchase total</div>

              <PriceWithCurrency
                price={{ rune: cartTotalInRune, usd: cartTotalInRune * (priceByToken?.RUNE || 1) }}
                size="lg"
                className="ml-auto"
              />
            </div>
          )}

          <div className={cn("mt-8 flex flex-col", isEmptyCart ? "items-center" : "items-end")}>
            <Button size="sm" className="px-8" {...submitCartButtonProps} />

            {/* TODO: Add other messages when multiple cart items are supported */}
            {!isEmptyCart && (
              <div className="mt-8 text-end text-[18px] italic text-[#F7FAFD] opacity-60">
                Only 1 item is allowed in cart during the alpha
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
